import React from 'react';

import { useMergeRefs } from '@floating-ui/react';

import { usePopoverContext } from './Provider';

export interface PopoverTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

export const PopoverTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & PopoverTriggerProps
>(function PopoverTrigger({ children, asChild = false, ...props }, propRef) {
  const context = usePopoverContext();
  const childrenRef = (children as any).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  if (asChild && React.isValidElement(children)) {
    // Destructure ref out of children.props so it doesn't overwrite the merged ref.
    // In React 19, ref is a regular prop, so spreading children.props would clobber
    // the merged ref that includes floating-ui's setReference callback.
    const { ref: _childRef, ...childProps } = children.props as Record<
      string,
      unknown
    >;
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...childProps,
        'data-state': context.open ? 'open' : 'closed',
      } as React.HTMLProps<HTMLElement>),
    );
  }

  return (
    <button
      ref={ref}
      data-state={context.open ? 'open' : 'closed'}
      type="button"
      {...context.getReferenceProps(props)}
    >
      {children}
    </button>
  );
});
